import { useState } from 'react'
import { clsx } from 'clsx'
import { X } from 'lucide-react'

type Props = {
  title?: string
  disabled?: boolean
  onCancel: () => void
  onSubmit: (reason: string) => void
}

const EXAMPLE_REASONS = [
  'Not something I would chase',
  'This is an ask of me, not something owed to me',
  'Appointment, booking, or FYI only',
  'Duplicate of an existing commitment',
]

export default function CommitmentDenyDialog({
  title = 'Why is this wrong?',
  disabled = false,
  onCancel,
  onSubmit,
}: Props) {
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()

  return (
    <div
      className="mt-2 w-full basis-full rounded-2xl border border-rose-200 bg-rose-50/90 p-3 text-rose-950 shadow-sm dark:border-rose-800/70 dark:bg-rose-950/40 dark:text-rose-100"
      data-testid="commitment-deny-dialog"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="mt-0.5 text-[11px] text-rose-700 dark:text-rose-200/80">
            This teaches the detector what to avoid next time.
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled}
          className="rounded-full p-1 text-rose-700 hover:bg-rose-100 disabled:opacity-60 dark:text-rose-200 dark:hover:bg-rose-900/50"
          aria-label="Cancel deny"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Example: this is only a calendar booking, not something anyone owes me."
        autoFocus
        rows={3}
        maxLength={1000}
        className="mt-2 w-full resize-none rounded-xl border border-rose-200 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 dark:border-rose-800 dark:bg-stone-950 dark:text-stone-100 dark:placeholder:text-stone-500"
      />

      <div className="mt-2 flex flex-wrap gap-1.5">
        {EXAMPLE_REASONS.map((example) => (
          <button
            key={example}
            type="button"
            onClick={() => setReason(example)}
            disabled={disabled}
            className="rounded-full border border-rose-200 bg-white/70 px-2 py-1 text-[11px] font-medium text-rose-800 disabled:opacity-60 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100"
          >
            {example}
          </button>
        ))}
      </div>

      <div className="mt-2 flex justify-end gap-1.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled}
          className="rounded-full px-3 py-1.5 text-xs font-semibold text-rose-800 disabled:opacity-60 dark:text-rose-100"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSubmit(trimmed)}
          disabled={disabled || !trimmed}
          className={clsx(
            'rounded-full px-3 py-1.5 text-xs font-semibold text-white',
            'bg-rose-600 hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50',
            'dark:bg-rose-500 dark:text-rose-950 dark:hover:bg-rose-400',
          )}
        >
          Deny & teach
        </button>
      </div>
    </div>
  )
}
